import React, { useEffect, useRef, useState } from 'react';

import cn from 'classnames';
import { useTranslation } from 'react-i18next';

import { Avatar, Flex, Input, UiButton, UiText } from '@shared/ui';
import { getEstimateInformationQuery } from '@widgets/EstimateInformation/api/hooks/getEstimateInformationQuery';
import { useCreateEstimateUserQuery } from '@widgets/EstimateInformation/api/hooks/useCreateEstimateUserQuery';
import { IEmployee } from '@widgets/EstimateInformation/model/types/types';

import cls from './AddInput.module.css';

interface AddInputProps {
    modalMode?: 'managers' | 'purchasers' | null;
    classes?: string;
    employees: IEmployee[];
    onChangeInputMode: (value: 'add' | 'exist' | null) => void;
    onSetAddingUserEmail?: (value: string | null) => void;
    value: string;
    onInput: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onSetEmailError: () => void;
    error: string | null;
}
const EMAIL_REGEX =
    /^(?!.*\.\.)(?!.*\.$)[A-Za-z0-9.!#$%&'*+/=?^_`{|}~-]+@(?:[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?\.)+[A-Za-z]{2,63}$/;

export const AddInput = ({
    classes,
    employees,
    onChangeInputMode,
    modalMode,
    onSetAddingUserEmail,
    value,
    onInput,
    onSetEmailError,
    error,
}: AddInputProps) => {
    const { t } = useTranslation();

    const createEstimateUserMutation = useCreateEstimateUserQuery();
    const [isShowList, setIsShowList] = useState(false);
    const [listStyle, setListStyle] = useState<React.CSSProperties>({});
    const { refetch } = getEstimateInformationQuery();
    const filteredEmployees =
        employees?.filter(
            (employee) =>
                employee?.name?.toLowerCase().includes(value.toLowerCase()) ||
                employee?.email?.toLowerCase().includes(value.toLowerCase())
        ) ?? [];

    const onFocus = () => {
        setIsShowList(true);
    };

    const inputRef = useRef<HTMLInputElement | null>(null);

    useEffect(() => {
        inputRef.current?.focus();
    }, []);
    const rootRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const calcRect = () => {
            if (!isShowList || error) {
                setListStyle({});
                return;
            }

            if (!rootRef.current) return;

            const rect = rootRef.current.getBoundingClientRect();
            const spaceBelow = window.innerHeight - rect.bottom;
            const openUpward = spaceBelow < 170;

            setListStyle({
                position: 'fixed',
                top: openUpward ? rect.top - 8 : rect.bottom + 4,
                left: rect.left,
                width: rect.width,
                transform: openUpward ? 'translateY(-100%)' : 'none',
            });
        };

        calcRect();

        window.addEventListener('resize', calcRect);
        window.addEventListener('scroll', calcRect, true);

        return () => {
            window.removeEventListener('resize', calcRect);
            window.removeEventListener('scroll', calcRect, true);
        };
    }, [error, isShowList]);

    useEffect(() => {
        const handleMouseDown = (e: MouseEvent) => {
            if (!rootRef.current) return;

            const target = e.target as Node;

            if (!rootRef.current.contains(target)) {
                onChangeInputMode?.(null);
                setIsShowList(false);
            }
        };

        document.addEventListener('mousedown', handleMouseDown);
        return () => document.removeEventListener('mousedown', handleMouseDown);
    }, [onChangeInputMode]);

    const onExist = async (email: string) => {
        const isValidEmail = EMAIL_REGEX.test(value.trim());

        if (!isValidEmail) {
            onSetEmailError();
            return;
        }

        const formData = new FormData();

        formData.append('role', modalMode === 'managers' ? 'manager' : 'purchase_agent');
        formData.append('email', email);

        await createEstimateUserMutation.mutateAsync(formData);
        await refetch();
        onChangeInputMode(null);
        onSetAddingUserEmail(email);
    };

    const onClickItem = async (email: string) => {
        const formData = new FormData();

        formData.append('role', modalMode === 'managers' ? 'manager' : 'purchase_agent');
        formData.append('email', email);

        await createEstimateUserMutation.mutateAsync(formData);
        await refetch();
        setIsShowList(false);
        onChangeInputMode(null);
        onSetAddingUserEmail(email);
    };
    return (
        <div className={cn(cls.AddInput, [classes])} ref={rootRef}>
            <Input
                error={error}
                maxLength={85}
                ref={inputRef}
                onFocus={onFocus}
                value={value}
                onInput={onInput}
                placeholder={t('AddInput.return.AddInput.Input.placeholder')}
                inputClassName={cls.input}
            />
            {isShowList && !error && (
                <ul
                    className={cls.employeeList}
                    style={listStyle}
                    onClick={(e) => {
                        e.stopPropagation();
                    }}
                >
                    {filteredEmployees.length > 0 ? (
                        filteredEmployees.map((employee) => (
                            <li
                                key={employee.id}
                                className={cls.employeeListItem}
                                onMouseDown={(e) => {
                                    e.stopPropagation();
                                    onClickItem(employee.email);
                                }}
                            >
                                <Avatar avatarUrl={employee.avatarUrl} />
                                <Flex direction="column" align="start">
                                    <UiText classes={cls.name} variant="mediumPlain">
                                        {employee.name ? employee.name : employee.email}
                                    </UiText>
                                    <UiText classes={cls.email} variant="mediumPlain">
                                        {employee.email}
                                    </UiText>
                                </Flex>
                            </li>
                        ))
                    ) : (
                        <li className={cn(cls.empty)}>
                            <UiText weight="medium" variant="boldPlain">
                                {t('AddInput.return.AddInput.employeeList.empty')}
                            </UiText>
                            <Flex direction="row" gap={8}>
                                <UiText
                                    classes={cls.emptyText}
                                    tag="span"
                                    weight="medium"
                                    variant="subPlain"
                                >
                                    {value}
                                </UiText>
                                <UiButton
                                    className={cls.button}
                                    btnText={t(
                                        'AddInput.return.AddInput.employeeList.empty.UiButton.btnText'
                                    )}
                                    variant="text"
                                    onMouseDown={async (e) => {
                                        e.stopPropagation();
                                        await onExist(value);
                                    }}
                                />
                            </Flex>
                        </li>
                    )}
                </ul>
            )}
        </div>
    );
};
